import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import offensiveApi from '../../services/offensiveApi';
import LoadingSpinner from '../LoadingSpinner';
import NormalizeTab from './NormalizeTab';

const FindingsManagementModal = ({ engagement, onClose, onFindingsUpdated }) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [findings, setFindings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingFindingId, setEditingFindingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isRefreshingEvidence, setIsRefreshingEvidence] = useState(false);
  const [isDraftingRisk, setIsDraftingRisk] = useState(false);
  const [findingToDelete, setFindingToDelete] = useState(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // Import state
  const [isImportingPentAGI, setIsImportingPentAGI] = useState(false);
  const [importLanguage, setImportLanguage] = useState('en');

  // Manual entry form state
  const [manualForm, setManualForm] = useState({
    // Basic Information
    title: '',
    finding_code: '',
    finding_category: 'vulnerability',
    finding_type: '',
    description: '',
    
    // Risk Assessment
    severity: 'Medium',
    // Annex B risk estimation — generated, not edited here. Kept in form state
    // so an edit round-trips the values instead of wiping them.
    amenazas: '',
    controles_mitigantes: '',
    amenazas_desc: '',
    controles_mitigantes_desc: '',
    exposure: '',
    impact: '',
    risk: '',
    cvss_score: '',
    cvss_vector: '',
    cve_id: '',

    // Affected Assets
    affected_assets: [],
    affected_asset_input: '',
    
    // Evidence & PoC
    proof_of_concept: '',
    tool_output: '',
    screenshots: [],
    
    // Remediation
    remediation: '',
    remediation_correctivas: [],
    remediation_preventivas: [],
    remediation_detectivas: [],
    remediation_note_input: '',
    remediation_type: 'correctivas', // correctivas, preventivas, detectivas
    
    // SAST-specific
    cwe_id: '',
    source_file: '',
    line_number: '',
    code_context: '',
    language: '',
    remediation_code_example: '',

    // DAST-specific
    owasp_category: '',
    request_sample: '',
    response_sample: '',

    // Shared scan metadata
    scan_tool: '',

    // Status
    status: 'open',
    retest_required: false,
    retest_status: 'not_required',
    retest_notes: ''
  });

  // Report type detection for conditional sections
  const subtypeId = engagement?.subtype_id;
  const isSAST = subtypeId === 4;
  const isDAST = subtypeId === 3;

  // File upload state
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  useEffect(() => {
    fetchFindings();
    // Imports run detached on the server and survive a refresh, a logout, even
    // a container restart — but the poll lives in this component, so without
    // this the UI comes back showing nothing in progress while the import is
    // still working. Reattach to whatever is already running.
    (async () => {
      try {
        const status = await offensiveApi.getImportStatus(engagement.id);
        if (status.status === 'running') {
          setIsImportingPentAGI(true);
          setSuccessMessage(describeImportStage(status));
          startImportPolling();
        }
      } catch (e) {
        // No job, or status unavailable — nothing to reattach to.
      }
    })();
  }, [engagement.id]);

  // Risk matrix matching backend risk_matrix.py
  // riesgo_matrix[impacto - 1][exposicion - 1] = riesgo
  const RIESGO_MATRIX = [
    // Impacto = 1 (Muy Bajo) - Row 0
    ['MUY BAJO', 'MUY BAJO', 'BAJO', 'BAJO', 'BAJO'],
    // Impacto = 2 (Bajo) - Row 1
    ['MUY BAJO', 'BAJO', 'BAJO', 'MEDIO', 'MEDIO'],
    // Impacto = 3 (Medio) - Row 2
    ['BAJO', 'BAJO', 'MEDIO', 'MEDIO', 'ALTO'],
    // Impacto = 4 (Alto) - Row 3
    ['BAJO', 'MEDIO', 'MEDIO', 'ALTO', 'MUY ALTO'],
    // Impacto = 5 (Muy Alto) - Row 4
    ['BAJO', 'MEDIO', 'ALTO', 'MUY ALTO', 'MUY ALTO']
  ];

  // Derive severity from CVSS score (FIRST scale)
  const deriveSeverityFromCvssScore = (score) => {
    if (!score || score === '' || isNaN(parseFloat(score))) return null;
    const cvssScore = parseFloat(score);
    
    if (cvssScore >= 9.0) return 'Critical';
    if (cvssScore >= 7.0) return 'High';
    if (cvssScore >= 4.0) return 'Medium';
    if (cvssScore > 0.0) return 'Low';
    return 'Info';
  };

  // Derive severity from exposure and impact (fallback)
  const deriveSeverityFromRiskMatrix = (exposure, impact) => {
    if (!exposure || !impact || exposure === '' || impact === '') return null;
    const exp = parseInt(exposure);
    const imp = parseInt(impact);
    if (isNaN(exp) || isNaN(imp) || exp < 1 || exp > 5 || imp < 1 || imp > 5) return null;
    
    // Get riesgo from matrix
    const riesgo = RIESGO_MATRIX[imp - 1][exp - 1];
    
    // Map riesgo to severity
    const riesgoToSeverity = {
      'MUY ALTO': 'Critical',
      'ALTO': 'High',
      'MEDIO': 'Medium',
      'BAJO': 'Low',
      'MUY BAJO': 'Info'
    };
    
    return riesgoToSeverity[riesgo] || 'Medium';
  };

  // Auto-calculate severity based on priority logic
  useEffect(() => {
    let isMounted = true;
    
    const calculateSeverity = async () => {
      let calculatedSeverity = null;
      
      // Priority 1: CVSS score provided
      if (manualForm.cvss_score && manualForm.cvss_score !== '') {
        calculatedSeverity = deriveSeverityFromCvssScore(manualForm.cvss_score);
      }
      // Priority 2: CVSS vector provided (no score)
      else if (manualForm.cvss_vector && manualForm.cvss_vector.trim() !== '' && (!manualForm.cvss_score || manualForm.cvss_score === '')) {
        try {
          const result = await offensiveApi.calculateCvssScore(manualForm.cvss_vector.trim());
          if (isMounted && result && result.cvss_score !== undefined) {
            // Update cvss_score with calculated value
            setManualForm(prev => ({ ...prev, cvss_score: result.cvss_score.toString() }));
            calculatedSeverity = deriveSeverityFromCvssScore(result.cvss_score);
          }
        } catch (error) {
          console.error('Error calculating CVSS score from vector:', error);
          // Fall through to next priority
        }
      }
      // Priority 3: Exposure + Impact (fallback)
      if (!calculatedSeverity) {
        calculatedSeverity = deriveSeverityFromRiskMatrix(manualForm.exposure, manualForm.impact);
      }
      // Priority 4: Default to Medium
      if (!calculatedSeverity) {
        calculatedSeverity = 'Medium';
      }
      
      // Update severity if different
      if (isMounted && calculatedSeverity && manualForm.severity !== calculatedSeverity) {
        setManualForm(prev => ({ ...prev, severity: calculatedSeverity }));
      }
    };

    calculateSeverity();
    
    return () => {
      isMounted = false;
    };
  }, [manualForm.cvss_score, manualForm.cvss_vector, manualForm.exposure, manualForm.impact]);

  // Calculate risk automatically from exposure and impact
  useEffect(() => {
    const exposure = parseInt(manualForm.exposure);
    const impact = parseInt(manualForm.impact);
    
    if (exposure && impact && !isNaN(exposure) && !isNaN(impact) && exposure >= 1 && exposure <= 5 && impact >= 1 && impact <= 5) {
      const calculatedRisk = exposure * impact;
      // Only update if the calculated risk is different from current value
      if (manualForm.risk !== calculatedRisk.toString()) {
        setManualForm(prev => ({ ...prev, risk: calculatedRisk.toString() }));
      }
    } else if ((!manualForm.exposure || !manualForm.impact) && manualForm.risk !== '') {
      // Clear risk if either exposure or impact is empty
      setManualForm(prev => ({ ...prev, risk: '' }));
    }
  }, [manualForm.exposure, manualForm.impact, manualForm.risk]);

  const fetchFindings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await offensiveApi.getOffensiveFindings(engagement.id);
      setFindings(data || []);
    } catch (error) {
      console.error('Error fetching findings:', error);
      setError(`Error loading findings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // An import runs for several minutes. Reporting the phase — and, during
  // normalization, how many sections are done — is the difference between a
  // progress indicator and a spinner.
  const IMPORT_STAGE_LABELS = {
    fetching: 'Retrieving assessment results',
    normalizing: 'Analyzing assessment results with AI',
    evidence: 'Attaching evidence',
    saving: 'Saving findings',
    risk_analysis: 'Drafting risk analysis',
    done: 'Finishing up',
  };

  const describeImportStage = (status) => {
    const label = IMPORT_STAGE_LABELS[status.stage] || 'Importing';
    return status.stage_detail ? `${label} — ${status.stage_detail}…` : `${label}…`;
  };

  // Poll handle, so leaving the modal mid-import doesn't leave it running.
  const importPollRef = useRef(null);
  useEffect(() => () => {
    if (importPollRef.current) clearInterval(importPollRef.current);
  }, []);

  // The backend decides this — it owns the reconciliation rule, and the UI
  // should not re-implement the arithmetic to guess at it.
  const findingsNeedingRiskAnalysis = findings.filter((f) => f.needs_risk_estimation).length;

  const handlePentAGIImport = async () => {
    setIsImportingPentAGI(true);
    setError(null);
    setSuccessMessage('Import started — analyzing assessment results with AI...');
    try {
      await offensiveApi.importPentAGIResults(engagement.id, { language: importLanguage });

      startImportPolling();
    } catch (err) {
      setIsImportingPentAGI(false);
      setError(`Import failed: ${err.response?.data?.detail || err.message}`);
      setSuccessMessage(null);
    }
  };

  // Poll an in-flight import. Every terminal state must stop the poll — it used
  // to only stop on completed/failed, so a cancelled or interrupted job left
  // this spinning forever.
  const startImportPolling = () => {
    if (importPollRef.current) clearInterval(importPollRef.current);
    const poll = setInterval(async () => {
      try {
        const status = await offensiveApi.getImportStatus(engagement.id);

        if (status.status === 'running') {
          setSuccessMessage(describeImportStage(status));
          return;
        }

        clearInterval(poll);
        importPollRef.current = null;
        setIsImportingPentAGI(false);

        if (status.status === 'completed') {
          setSuccessMessage(`Imported ${status.imported} finding${status.imported !== 1 ? 's' : ''} from assessment`);
          fetchFindings();
          if (onFindingsUpdated) onFindingsUpdated();
          setTimeout(() => setSuccessMessage(null), 8000);
        } else {
          setSuccessMessage(null);
          const detail = status.error
            ? `: ${status.error}`
            : status.status === 'interrupted'
              ? ' — the import stopped responding and was probably killed. Re-run it; any completed analysis is reused.'
              : '';
          setError(`Import ${status.status}${detail}`);
          // Partial imports still land, so show whatever was created.
          fetchFindings();
        }
      } catch (pollErr) {
        // Polling error is transient, keep trying
      }
    }, 5000);
    importPollRef.current = poll;
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate required fields
      if (!manualForm.title.trim()) {
        setError('Title is required');
        setIsSubmitting(false);
        return;
      }
      if (!manualForm.finding_type.trim()) {
        setError('Finding type is required');
        setIsSubmitting(false);
        return;
      }
      if (!manualForm.description.trim()) {
        setError('Description is required');
        setIsSubmitting(false);
        return;
      }

      // Build remediation_structured if any remediation notes exist
      const remediation_structured = {};
      if (manualForm.remediation_correctivas.length > 0) {
        remediation_structured.correctivas = manualForm.remediation_correctivas;
      }
      if (manualForm.remediation_preventivas.length > 0) {
        remediation_structured.preventivas = manualForm.remediation_preventivas;
      }
      if (manualForm.remediation_detectivas.length > 0) {
        remediation_structured.detectivas = manualForm.remediation_detectivas;
      }

      const findingData = {
        // Basic Information
        title: manualForm.title.trim(),
        finding_code: manualForm.finding_code.trim() || null,
        finding_category: manualForm.finding_category.trim() || null,
        finding_type: manualForm.finding_type.trim(),
        description: manualForm.description.trim(),
        
        // Risk Assessment
        severity: manualForm.severity.trim() || null,
        amenazas: manualForm.amenazas ? parseInt(manualForm.amenazas) : null,
        controles_mitigantes: manualForm.controles_mitigantes ? parseInt(manualForm.controles_mitigantes) : null,
        amenazas_desc: manualForm.amenazas_desc.trim() || null,
        controles_mitigantes_desc: manualForm.controles_mitigantes_desc.trim() || null,
        exposure: manualForm.exposure ? parseInt(manualForm.exposure) : null,
        impact: manualForm.impact ? parseInt(manualForm.impact) : null,
        risk: manualForm.risk ? parseInt(manualForm.risk) : null,
        cvss_score: manualForm.cvss_score ? parseFloat(manualForm.cvss_score) : null,
        cvss_vector: manualForm.cvss_vector.trim() || null,
        cve_id: manualForm.cve_id.trim() || null,
        
        // Affected Assets
        affected_assets: manualForm.affected_assets,
        
        // Evidence & PoC
        proof_of_concept: manualForm.proof_of_concept.trim() || null,
        tool_output: manualForm.tool_output.trim() || null,
        screenshots: manualForm.screenshots.length > 0 ? manualForm.screenshots : null,
        
        // Remediation
        remediation: manualForm.remediation.trim() || null,
        remediation_structured: Object.keys(remediation_structured).length > 0 ? remediation_structured : null,

        // SAST-specific
        cwe_id: manualForm.cwe_id.trim() || null,
        source_file: manualForm.source_file.trim() || null,
        line_number: manualForm.line_number ? parseInt(manualForm.line_number) : null,
        code_context: manualForm.code_context.trim() || null,
        language: manualForm.language.trim() || null,
        remediation_code_example: manualForm.remediation_code_example.trim() || null,

        // DAST-specific
        owasp_category: manualForm.owasp_category.trim() || null,
        request_sample: manualForm.request_sample.trim() || null,
        response_sample: manualForm.response_sample.trim() || null,

        // Shared scan metadata
        scan_tool: manualForm.scan_tool.trim() || null,

        // Status
        status: manualForm.status,
        retest_required: manualForm.retest_required,
        retest_status: manualForm.retest_status,
        retest_notes: manualForm.retest_notes.trim() || null
      };

      if (editingFindingId) {
        // Update existing finding
        await offensiveApi.updateOffensiveFinding(editingFindingId, findingData);
        setSuccessMessage('Finding updated successfully');
        setEditingFindingId(null);
      } else {
        // Create new finding
        await offensiveApi.addOffensiveFinding(engagement.id, findingData);
        setSuccessMessage('Finding added successfully');
      }
      
      resetManualForm();
      fetchFindings();
      
      if (onFindingsUpdated) {
        onFindingsUpdated();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error adding finding:', error);
      setError(`Error adding finding: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetManualForm = () => {
    setEditingFindingId(null);
    setManualForm({
      title: '',
      finding_code: '',
      finding_category: 'vulnerability',
      finding_type: '',
      description: '',
      severity: 'Medium',
      amenazas: '',
      controles_mitigantes: '',
      amenazas_desc: '',
      controles_mitigantes_desc: '',
      exposure: '',
      impact: '',
      risk: '',
      cvss_score: '',
      cvss_vector: '',
      cve_id: '',
      affected_assets: [],
      affected_asset_input: '',
      proof_of_concept: '',
      tool_output: '',
      screenshots: [],
      remediation: '',
      remediation_correctivas: [],
      remediation_preventivas: [],
      remediation_detectivas: [],
      remediation_note_input: '',
      remediation_type: 'correctivas',
      cwe_id: '',
      source_file: '',
      line_number: '',
      code_context: '',
      language: '',
      remediation_code_example: '',
      owasp_category: '',
      request_sample: '',
      response_sample: '',
      scan_tool: '',
      status: 'open',
      retest_required: false,
      retest_status: 'not_required',
      retest_notes: ''
    });
  };

  // Helper functions for managing arrays
  const addAffectedAsset = () => {
    if (manualForm.affected_asset_input.trim()) {
      setManualForm({
        ...manualForm,
        affected_assets: [...manualForm.affected_assets, manualForm.affected_asset_input.trim()],
        affected_asset_input: ''
      });
    }
  };

  const removeAffectedAsset = (index) => {
    setManualForm({
      ...manualForm,
      affected_assets: manualForm.affected_assets.filter((_, i) => i !== index)
    });
  };

  const addRemediationNote = () => {
    if (manualForm.remediation_note_input.trim()) {
      const key = `remediation_${manualForm.remediation_type}`;
      setManualForm({
        ...manualForm,
        [key]: [...manualForm[key], manualForm.remediation_note_input.trim()],
        remediation_note_input: ''
      });
    }
  };

  const removeRemediationNote = (type, index) => {
    const key = `remediation_${type}`;
    setManualForm({
      ...manualForm,
      [key]: manualForm[key].filter((_, i) => i !== index)
    });
  };

  const handleScreenshotUpload = (e) => {
    let files;
    if (e.dataTransfer) {
      files = Array.from(e.dataTransfer.files);
    } else if (e.target) {
      files = Array.from(e.target.files);
    }
    
    if (files && files.length > 0) {
      // Filter only image files
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      imageFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          setManualForm({
            ...manualForm,
            screenshots: [...manualForm.screenshots, { image: base64String, caption: '' }]
          });
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleScreenshotDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleScreenshotUpload(e);
  };

  const handleScreenshotDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleScreenshotDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleScreenshotDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleScreenshotClick = () => {
    document.getElementById('screenshot-file-input').click();
  };

  const updateScreenshotCaption = (index, caption) => {
    const updated = [...manualForm.screenshots];
    updated[index] = { ...updated[index], caption };
    setManualForm({ ...manualForm, screenshots: updated });
  };

  const removeScreenshot = (index) => {
    setManualForm({
      ...manualForm,
      screenshots: manualForm.screenshots.filter((_, i) => i !== index)
    });
  };

  const handleFileSelect = (e) => {
    let file;
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Validate file type (CherryTree .ctb/.ctd or YAML-ZIP .zip)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.ctb') && !fileName.endsWith('.ctd') && !fileName.endsWith('.zip')) {
        setError('Please select a valid file: CherryTree (.ctb or .ctd) or YAML-ZIP (.zip)');
        return;
      }
      setImportFile(file);
      setError(null);
    }
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e);
  };

  const handleImportDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImportDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImportDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImportClick = () => {
    document.getElementById('import-file-input').click();
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Por favor selecciona un archivo para importar');
      return;
    }

    setIsImporting(true);
    setError(null);
    setSuccessMessage(null);
    setImportProgress('Importing findings...');

    try {
      const result = await offensiveApi.importFindings(engagement.id, importFile);
      
      const count = result.count || result.imported_count || 0;
      setSuccessMessage(`Successfully imported ${count} finding${count !== 1 ? 's' : ''}`);
      setImportFile(null);
      setImportProgress(null);
      
      // Reset file input
      const fileInput = document.getElementById('import-file-input');
      if (fileInput) fileInput.value = '';

      fetchFindings();
      
      if (onFindingsUpdated) {
        onFindingsUpdated();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error importing findings:', error);
      setError(`Error al importar: ${error.response?.data?.detail || error.message}`);
      setImportProgress(null);
    } finally {
      setIsImporting(false);
    }
  };

  const handleEdit = (finding) => {
    // Populate form with finding data
    setManualForm({
      title: finding.title || '',
      finding_code: finding.finding_code || '',
      finding_category: finding.finding_category || 'vulnerability',
      finding_type: finding.finding_type || '',
      description: finding.description || '',
      severity: finding.severity || 'Medium',
      amenazas: finding.amenazas?.toString() || '',
      controles_mitigantes: finding.controles_mitigantes?.toString() || '',
      amenazas_desc: finding.amenazas_desc || '',
      controles_mitigantes_desc: finding.controles_mitigantes_desc || '',
      exposure: finding.exposure?.toString() || '',
      impact: finding.impact?.toString() || '',
      risk: finding.risk?.toString() || '',
      cvss_score: finding.cvss_score?.toString() || '',
      cvss_vector: finding.cvss_vector || '',
      cve_id: finding.cve_id || '',
      affected_assets: finding.affected_assets || [],
      affected_asset_input: '',
      proof_of_concept: finding.proof_of_concept || '',
      tool_output: finding.tool_output || '',
      screenshots: finding.screenshots || [],
      remediation: finding.remediation || '',
      remediation_correctivas: finding.remediation_structured?.correctivas || [],
      remediation_preventivas: finding.remediation_structured?.preventivas || [],
      remediation_detectivas: finding.remediation_structured?.detectivas || [],
      remediation_note_input: '',
      remediation_type: 'correctivas',
      cwe_id: finding.cwe_id || '',
      source_file: finding.source_file || '',
      line_number: finding.line_number?.toString() || '',
      code_context: finding.code_context || '',
      language: finding.language || '',
      remediation_code_example: finding.remediation_code_example || '',
      owasp_category: finding.owasp_category || '',
      request_sample: finding.request_sample || '',
      response_sample: finding.response_sample || '',
      scan_tool: finding.scan_tool || '',
      status: finding.status || 'open',
      retest_required: finding.retest_required || false,
      retest_status: finding.retest_status || 'not_required',
      retest_notes: finding.retest_notes || ''
    });
    setEditingFindingId(finding.id);
    setActiveTab('manual');
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (findingId) => {
    setFindingToDelete(findingId);
    setShowDeleteModal(true);
  };

  // Re-attaches PentAGI evidence to existing findings from their stored subtask
  // provenance. Findings imported before provenance was recorded can't be
  // matched and are reported back rather than guessed at.
  const handleRefreshEvidence = async () => {
    setIsRefreshingEvidence(true);
    setError(null);
    try {
      const result = await offensiveApi.refreshPentAGIEvidence(engagement.id);
      const skipped = result.skipped_no_provenance
        ? ` — ${result.skipped_no_provenance} skipped (imported before provenance was tracked)`
        : '';
      setSuccessMessage(`Evidence refreshed on ${result.updated} of ${result.considered} findings${skipped}`);
      fetchFindings();
      if (onFindingsUpdated) onFindingsUpdated();
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (error) {
      console.error('Error refreshing evidence:', error);
      setError(`Evidence refresh error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsRefreshingEvidence(false);
    }
  };

  // Imports draft this automatically; this covers findings added by hand, ones
  // imported before that was wired, and re-drafting over existing estimates.
  const handleDraftRiskAnalysis = async (overwrite = false) => {
    setIsDraftingRisk(true);
    setError(null);
    try {
      const result = await offensiveApi.draftFindingsRiskAnalysis(engagement.id, { overwrite });
      setSuccessMessage(
        result.drafted
          ? `Risk estimation drafted for ${result.drafted} finding(s). Review before delivery.`
          : 'All findings already have a risk estimation'
      );
      fetchFindings();
      if (onFindingsUpdated) onFindingsUpdated();
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (error) {
      console.error('Error drafting risk analysis:', error);
      setError(`Risk analysis error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDraftingRisk(false);
    }
  };

  // Bulk delete exists for the re-import cycle: importing a flow always creates
  // findings rather than updating them, so a clean re-import means clearing the
  // engagement first. Irreversible, hence its own confirmation showing the count.
  const handleBulkDeleteConfirm = async () => {
    setIsBulkDeleting(true);
    try {
      const result = await offensiveApi.deleteAllOffensiveFindings(engagement.id);
      setSuccessMessage(`${result.deleted} finding(s) deleted`);
      fetchFindings();
      if (onFindingsUpdated) onFindingsUpdated();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (error) {
      console.error('Error deleting all findings:', error);
      setError(`Delete error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsBulkDeleting(false);
      setShowBulkDeleteModal(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!findingToDelete) return;

    try {
      await offensiveApi.deleteOffensiveFinding(findingToDelete);
      setSuccessMessage('Finding deleted successfully');
      fetchFindings();
      
      if (onFindingsUpdated) {
        onFindingsUpdated();
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting finding:', error);
      setError(`Delete error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setShowDeleteModal(false);
      setFindingToDelete(null);
    }
  };

  // Get qualitative risk level from exposure and impact using the risk matrix
  const getRiskLevel = (exposure, impact) => {
    if (!exposure || !impact || exposure === '' || impact === '') return null;
    const exp = parseInt(exposure);
    const imp = parseInt(impact);
    if (isNaN(exp) || isNaN(imp) || exp < 1 || exp > 5 || imp < 1 || imp > 5) return null;
    
    // Get riesgo from matrix (0-indexed)
    const riesgo = RIESGO_MATRIX[imp - 1][exp - 1];
    
    // Map to user-friendly labels
    const riesgoMap = {
      'MUY BAJO': 'Informativo',
      'BAJO': 'Bajo',
      'MEDIO': 'Medio',
      'ALTO': 'Alto',
      'MUY ALTO': 'Critical'
    };
    
    return riesgoMap[riesgo] || null;
  };

  // Get color for risk level
  const getRiskLevelColor = (exposure, impact) => {
    const level = getRiskLevel(exposure, impact);
    if (!level) return 'text-slate-400';
    
    switch (level) {
      case 'Informativo':
        return 'text-blue-400';
      case 'Bajo':
        return 'text-green-400';
      case 'Medio':
        return 'text-yellow-400';
      case 'Alto':
        return 'text-orange-400';
      case 'Critical':
        return 'text-rose-400';
      default:
        return 'text-slate-400';
    }
  };

  const getSeverityColor = (severity) => {
    if (!severity) return 'bg-slate-50 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300';
    const severityLower = severity.toLowerCase();
    const colors = {
      low: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      medium: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      high: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      critical: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
      info: 'bg-slate-50 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300'
    };
    return colors[severityLower] || colors.medium;
  };

  // Get text color for severity (for inline display)
  const getSeverityTextColor = (severity) => {
    if (!severity) return 'text-slate-400';
    const severityLower = severity.toLowerCase();
    const colors = {
      critical: 'text-rose-400',
      high: 'text-orange-400',
      medium: 'text-yellow-400',
      low: 'text-blue-400',
      info: 'text-slate-400'
    };
    return colors[severityLower] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-slate-50 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300',
      validated: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      verified: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      false_positive: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      remediated: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
    };
    return colors[status] || colors.open;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-auto rounded-2xl bg-slate-900/90 shadow-2xl border border-slate-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Manage Findings</h3>
            <p className="text-sm text-slate-400 mt-1">{engagement.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          <button
            onClick={() => setActiveTab('pentagi')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'pentagi'
                ? 'text-violet-400 border-b-2 border-violet-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Import Findings
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'manual'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Add / Edit
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'import'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            File Import
          </button>
          <button
            onClick={() => setActiveTab('normalize')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'normalize'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            AI Normalize
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'list'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Findings ({findings.length})
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-900/30 border border-rose-800 text-rose-300 text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-900/30 border border-emerald-800 text-emerald-300 text-sm">
            {successMessage}
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'manual' && (
          <form onSubmit={handleManualSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
            {editingFindingId && (
              <div className="mb-4 p-3 rounded-lg bg-indigo-900/30 border border-indigo-800 text-indigo-300 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>Editing finding. Changes will be saved on submit.</span>
                  <button
                    type="button"
                    onClick={() => {
                      resetManualForm();
                      setEditingFindingId(null);
                    }}
                    className="ml-auto text-indigo-400 hover:text-indigo-200"
                  >
                    Cancel edit
                  </button>
                </div>
              </div>
            )}
            {/* Basic Information Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Basic Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Title <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={manualForm.title}
                  onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Code
                  </label>
                  <input
                    type="text"
                    value={manualForm.finding_code}
                    onChange={(e) => setManualForm({ ...manualForm, finding_code: e.target.value })}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Category
                  </label>
                  <select
                    value={manualForm.finding_category}
                    onChange={(e) => setManualForm({ ...manualForm, finding_category: e.target.value })}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="strength">Fortaleza</option>
                    <option value="vulnerability">Vulnerabilidad</option>
                    <option value="informational">Informativo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Finding Type <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={manualForm.finding_type}
                  onChange={(e) => setManualForm({ ...manualForm, finding_type: e.target.value })}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description <span className="text-rose-400">*</span>
                </label>
                <textarea
                  value={manualForm.description}
                  onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                  rows={5}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Risk Assessment Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Risk Assessment</h4>
              
              {/* Severity & CVSS Section */}
              <div className="space-y-4">
                <h5 className="text-xs font-semibold text-slate-400 uppercase">Technical Severity (CVSS)</h5>
                
                {/* Horizontal Flow Layout */}
                <div className="flex items-center gap-4 relative">
                  {/* Input Fields Group */}
                  <div className="w-80 space-y-3 relative">
                    {/* CVE ID */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">
                        CVE ID
                      </label>
                      <input
                        type="text"
                        value={manualForm.cve_id}
                        onChange={(e) => setManualForm({ ...manualForm, cve_id: e.target.value })}
                        placeholder="CVE-2021-12345"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                      />
                    </div>
                    
                    {/* CVSS Score */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">
                        CVSS Score
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={manualForm.cvss_score}
                        onChange={(e) => setManualForm({ ...manualForm, cvss_score: e.target.value })}
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    
                    {/* CVSS Vector */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">
                        CVSS Vector
                      </label>
                      <input
                        type="text"
                        value={manualForm.cvss_vector}
                        onChange={(e) => setManualForm({ ...manualForm, cvss_vector: e.target.value })}
                        placeholder="CVSS:3.0/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H"
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                      />
                    </div>
                  </div>
                  
                  {/* Connector Section with lines */}
                  <div className="flex-1 relative flex items-center" style={{ minHeight: '120px' }}>
                    
                    {/* Horizontal line connecting all inputs through vertical line to result */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-px bg-indigo-500/50"></div>
                    
                    {/* Arrow centered on the intersection with background to cover line */}
                    <div className="relative z-10 flex items-center justify-center mx-auto">
                      <div className="w-8 h-8 rounded-full bg-slate-950 flex items-center justify-center border-2 border-slate-800">
                        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Calculated Severidad */}
                  <div className="w-64 p-4 rounded-lg bg-gradient-to-br from-indigo-950/40 via-indigo-900/20 to-slate-950/40 border-2 border-indigo-500/30">
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Severidad
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value=""
                        className="w-full rounded-md bg-slate-950/70 border border-indigo-500/30 p-2.5 text-slate-200 text-sm cursor-not-allowed"
                      />
                      {manualForm.severity && (
                        <div className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold ${getSeverityTextColor(manualForm.severity)} pointer-events-none`}>
                          {manualForm.severity === 'Critical' ? 'Critical' :
                           manualForm.severity === 'High' ? 'Alta' :
                           manualForm.severity === 'Medium' ? 'Media' :
                           manualForm.severity === 'Low' ? 'Baja' : 'Info'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual Separator */}
              <div className="border-t border-slate-700/50 my-4"></div>

              {/* Risk Section (Exposure × Impact) */}
              <div className="space-y-4">
                <h5 className="text-xs font-semibold text-slate-400 uppercase">Contextual Risk</h5>
                
                {/* Horizontal Flow Layout */}
                <div className="flex items-center gap-4 relative">
                  {/* Input Fields Group */}
                  <div className="w-80 space-y-3 relative">
                    <div className="relative">
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">
                        Exposure Factor (1-5)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={manualForm.exposure}
                        onChange={(e) => setManualForm({ ...manualForm, exposure: e.target.value })}
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">
                        Impact (1-5)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={manualForm.impact}
                        onChange={(e) => setManualForm({ ...manualForm, impact: e.target.value })}
                        className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  
                  {/* Connector Section with lines */}
                  <div className="flex-1 relative flex items-center" style={{ minHeight: '80px' }}>
                    
                    {/* Horizontal line connecting all inputs through vertical line to result */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-px bg-violet-500/50"></div>
                    
                    {/* Arrow centered on the intersection with background to cover line */}
                    <div className="relative z-10 flex items-center justify-center mx-auto">
                      <div className="w-8 h-8 rounded-full bg-slate-950 flex items-center justify-center border-2 border-slate-800">
                        <svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Calculated Risk */}
                  <div className="w-64 p-4 rounded-lg bg-gradient-to-br from-violet-950/40 via-violet-900/20 to-slate-950/40 border-2 border-violet-500/30">
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Risk (1-25)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="25"
                        value={manualForm.risk}
                        readOnly
                        className="w-full rounded-md bg-slate-950/70 border border-violet-500/30 p-2.5 text-slate-200 text-sm cursor-not-allowed pr-20"
                      />
                      {manualForm.exposure && manualForm.impact && getRiskLevel(manualForm.exposure, manualForm.impact) ? (
                        <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold ${getRiskLevelColor(manualForm.exposure, manualForm.impact)} pointer-events-none`}>
                          {getRiskLevel(manualForm.exposure, manualForm.impact)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Affected Assets Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Activos Afectados</h4>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualForm.affected_asset_input}
                  onChange={(e) => setManualForm({ ...manualForm, affected_asset_input: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAffectedAsset())}
                  placeholder="IP, dominio, hostname..."
                  className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                />
                <button
                  type="button"
                  onClick={addAffectedAsset}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md transition-colors"
                >
                  Add
                </button>
              </div>

              {manualForm.affected_assets.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {manualForm.affected_assets.map((asset, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-slate-800/50 border border-slate-700 text-slate-300 text-sm"
                    >
                      {asset}
                      <button
                        type="button"
                        onClick={() => removeAffectedAsset(idx)}
                        className="text-slate-400 hover:text-rose-400"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Evidence & PoC Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Evidencia y Prueba de Concepto</h4>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Prueba de Concepto
                </label>
                <textarea
                  value={manualForm.proof_of_concept}
                  onChange={(e) => setManualForm({ ...manualForm, proof_of_concept: e.target.value })}
                  rows={4}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  placeholder="Exploitation steps, commands executed..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Salida de Herramienta
                </label>
                <textarea
                  value={manualForm.tool_output}
                  onChange={(e) => setManualForm({ ...manualForm, tool_output: e.target.value })}
                  rows={6}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  placeholder="Salida cruda de herramientas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Capturas de Pantalla
                </label>
                <div
                  className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-slate-600 transition-colors"
                  onDrop={handleScreenshotDrop}
                  onDragOver={handleScreenshotDragOver}
                  onDragEnter={handleScreenshotDragEnter}
                  onDragLeave={handleScreenshotDragLeave}
                  onClick={handleScreenshotClick}
                >
                  <svg className="w-10 h-10 mx-auto text-slate-400 dark:text-slate-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                    Drag and drop images or click to select
                  </p>
                  <button
                    type="button"
                    className="text-xs text-indigo-400 hover:text-indigo-300 underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleScreenshotClick();
                    }}
                  >
                    O elige archivos
                  </button>
                </div>
                <input
                  id="screenshot-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleScreenshotUpload}
                  className="hidden"
                />
                {manualForm.screenshots.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {manualForm.screenshots.map((screenshot, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-md bg-slate-900/50 border border-slate-800">
                        <img src={screenshot.image} alt={`Screenshot ${idx + 1}`} className="w-24 h-24 object-cover rounded" />
                        <div className="flex-1">
                          <input
                            type="text"
                            value={screenshot.caption}
                            onChange={(e) => updateScreenshotCaption(idx, e.target.value)}
                            placeholder="Description de la captura..."
                            className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm mb-2 placeholder-slate-600"
                          />
                          <button
                            type="button"
                            onClick={() => removeScreenshot(idx)}
                            className="text-xs text-rose-400 hover:text-rose-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SAST-specific Section */}
            {isSAST && (
              <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
                <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Static Analysis Details</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">CWE ID</label>
                    <input
                      type="text"
                      value={manualForm.cwe_id}
                      onChange={(e) => setManualForm({ ...manualForm, cwe_id: e.target.value })}
                      placeholder="CWE-89"
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Language</label>
                    <input
                      type="text"
                      value={manualForm.language}
                      onChange={(e) => setManualForm({ ...manualForm, language: e.target.value })}
                      placeholder="python, java, typescript..."
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Source File</label>
                    <input
                      type="text"
                      value={manualForm.source_file}
                      onChange={(e) => setManualForm({ ...manualForm, source_file: e.target.value })}
                      placeholder="src/auth/login.py"
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Line</label>
                    <input
                      type="number"
                      value={manualForm.line_number}
                      onChange={(e) => setManualForm({ ...manualForm, line_number: e.target.value })}
                      placeholder="42"
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Vulnerable Code Snippet</label>
                  <textarea
                    value={manualForm.code_context}
                    onChange={(e) => setManualForm({ ...manualForm, code_context: e.target.value })}
                    rows={6}
                    placeholder="Paste the vulnerable code fragment..."
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Remediation Code Example</label>
                  <textarea
                    value={manualForm.remediation_code_example}
                    onChange={(e) => setManualForm({ ...manualForm, remediation_code_example: e.target.value })}
                    rows={6}
                    placeholder="Paste the fixed code example..."
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Scan Tool</label>
                  <input
                    type="text"
                    value={manualForm.scan_tool}
                    onChange={(e) => setManualForm({ ...manualForm, scan_tool: e.target.value })}
                    placeholder="SonarQube, Semgrep, Fortify..."
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            )}

            {/* DAST-specific Section */}
            {isDAST && (
              <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
                <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Dynamic Test Details</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">OWASP Category</label>
                    <input
                      type="text"
                      value={manualForm.owasp_category}
                      onChange={(e) => setManualForm({ ...manualForm, owasp_category: e.target.value })}
                      placeholder="A03:2021-Injection"
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Scan Tool</label>
                    <input
                      type="text"
                      value={manualForm.scan_tool}
                      onChange={(e) => setManualForm({ ...manualForm, scan_tool: e.target.value })}
                      placeholder="Burp Suite, OWASP ZAP..."
                      className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">HTTP Request Sample</label>
                  <textarea
                    value={manualForm.request_sample}
                    onChange={(e) => setManualForm({ ...manualForm, request_sample: e.target.value })}
                    rows={6}
                    placeholder={"POST /api/login HTTP/1.1\nHost: app.example.com\nContent-Type: application/json\n\n{\"username\":\"...\"}"}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">HTTP Response Sample</label>
                  <textarea
                    value={manualForm.response_sample}
                    onChange={(e) => setManualForm({ ...manualForm, response_sample: e.target.value })}
                    rows={6}
                    placeholder={"HTTP/1.1 200 OK\nContent-Type: application/json\n\n{\"error\":\"...\"}"}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                  />
                </div>
              </div>
            )}

            {/* Remediation Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Remediation</h4>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Remediation (Texto Libre)
                </label>
                <textarea
                  value={manualForm.remediation}
                  onChange={(e) => setManualForm({ ...manualForm, remediation: e.target.value })}
                  rows={4}
                  className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Remediation Estructurada
                </label>
                <div className="flex gap-2 mb-3 items-center">
                  <select
                    value={manualForm.remediation_type}
                    onChange={(e) => setManualForm({ ...manualForm, remediation_type: e.target.value })}
                    className="rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 self-center"
                  >
                    <option value="correctivas">Correctivas</option>
                    <option value="preventivas">Preventivas</option>
                    <option value="detectivas">Detectivas</option>
                  </select>
                  <textarea
                    value={manualForm.remediation_note_input}
                    onChange={(e) => setManualForm({ ...manualForm, remediation_note_input: e.target.value })}
                    rows={2}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600 resize-y"
                  />
                  <button
                    type="button"
                    onClick={addRemediationNote}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md transition-colors self-center whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>

                {manualForm.remediation_correctivas.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-emerald-400 mb-1">Correctivas:</div>
                    <ul className="space-y-1">
                      {manualForm.remediation_correctivas.map((note, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-emerald-400">•</span>
                          <span className="flex-1">{note}</span>
                          <button
                            type="button"
                            onClick={() => removeRemediationNote('correctivas', idx)}
                            className="text-rose-400 hover:text-rose-300"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {manualForm.remediation_preventivas.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-blue-400 mb-1">Preventivas:</div>
                    <ul className="space-y-1">
                      {manualForm.remediation_preventivas.map((note, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-blue-400">•</span>
                          <span className="flex-1">{note}</span>
                          <button
                            type="button"
                            onClick={() => removeRemediationNote('preventivas', idx)}
                            className="text-rose-400 hover:text-rose-300"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {manualForm.remediation_detectivas.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-amber-400 mb-1">Detectivas:</div>
                    <ul className="space-y-1">
                      {manualForm.remediation_detectivas.map((note, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-amber-400">•</span>
                          <span className="flex-1">{note}</span>
                          <button
                            type="button"
                            onClick={() => removeRemediationNote('detectivas', idx)}
                            className="text-rose-400 hover:text-rose-300"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Status & Retest Section */}
            <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
              <h4 className="text-sm font-semibold text-indigo-400 uppercase mb-3">Status & Retest</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Estado
                  </label>
                  <select
                    value={manualForm.status}
                    onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="open">Open</option>
                    <option value="validated">Validated</option>
                    <option value="false_positive">False Positive</option>
                    <option value="remediated">Remediated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Retest Status
                  </label>
                  <select
                    value={manualForm.retest_status}
                    onChange={(e) => setManualForm({ ...manualForm, retest_status: e.target.value })}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="not_required">Not Required</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="verified">Verified</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualForm.retest_required}
                    onChange={(e) => setManualForm({ ...manualForm, retest_required: e.target.checked })}
                    className="rounded border-slate-700 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-300">Retest Required</span>
                </label>
              </div>

              {manualForm.retest_required && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Retest Notes
                  </label>
                  <textarea
                    value={manualForm.retest_notes}
                    onChange={(e) => setManualForm({ ...manualForm, retest_notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
                    placeholder="Retest observations and results..."
                  />
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting 
                  ? 'Saving...' 
                  : editingFindingId 
                    ? 'Actualizar' 
                    : 'Add Finding'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'import' && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-indigo-950/30 border border-indigo-900/50">
              <p className="text-sm text-indigo-300">
                <strong>Import Findings:</strong> Select a CherryTree (.ctb or .ctd) or YAML-ZIP (.zip) file to import findings automatically.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Import File
              </label>
              <div
                className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-slate-600 transition-colors"
                onDrop={handleImportDrop}
                onDragOver={handleImportDragOver}
                onDragEnter={handleImportDragEnter}
                onDragLeave={handleImportDragLeave}
                onClick={handleImportClick}
              >
                <svg className="w-10 h-10 mx-auto text-slate-400 dark:text-slate-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                  Drag and drop file (.ctb, .ctd or .zip) or click to select
                </p>
                <button
                  type="button"
                  className="text-xs text-indigo-400 hover:text-indigo-300 underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImportClick();
                  }}
                >
                  O elige archivo
                </button>
              </div>
              <input
                id="import-file-input"
                type="file"
                accept=".ctb,.ctd,.zip"
                onChange={handleFileSelect}
                className="hidden"
              />
              {importFile && (
                <div className="mt-3 p-3 rounded-md bg-slate-900/50 border border-slate-800">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-300">
                      <span className="text-slate-500">Archivo seleccionado:</span> {importFile.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setImportFile(null);
                        const fileInput = document.getElementById('import-file-input');
                        if (fileInput) fileInput.value = '';
                      }}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {importProgress && (
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-slate-300">{importProgress}</span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isImporting ? 'Importing...' : 'Import Findings'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'normalize' && (
          <NormalizeTab engagement={engagement} onFindingsImported={() => { fetchFindings(); if (onFindingsUpdated) onFindingsUpdated(); }} />
        )}

        {activeTab === 'pentagi' && (
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-violet-950/30 border border-violet-900/50">
              <p className="text-sm text-violet-300">
                Import and normalize findings from the automated assessment. Results will be structured into editable findings with severity, remediation, and OWASP/CWE mappings.
              </p>
            </div>

            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <svg className="w-16 h-16 text-violet-500/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <p className="text-slate-400 text-sm text-center max-w-md">
                Pull findings from the completed assessment flows. Existing findings won't be duplicated.
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400">Language:</span>
                <div className="inline-flex rounded-lg border border-slate-700 bg-slate-800/50 p-0.5">
                  <button
                    type="button"
                    onClick={() => !isImportingPentAGI && setImportLanguage('en')}
                    disabled={isImportingPentAGI}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-all ${
                      importLanguage === 'en'
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-slate-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    onClick={() => !isImportingPentAGI && setImportLanguage('es')}
                    disabled={isImportingPentAGI}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-all ${
                      importLanguage === 'es'
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-slate-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    ES
                  </button>
                </div>
              </div>
              <button
                onClick={handlePentAGIImport}
                disabled={isImportingPentAGI}
                className="px-6 py-3 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isImportingPentAGI ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Importing findings...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Import Findings
                  </>
                )}
              </button>
              {findings.length > 0 && (
                <p className="text-xs text-slate-500">{findings.length} finding{findings.length !== 1 ? 's' : ''} already imported</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'list' && (
          <div className="space-y-4">
            {findings.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-800">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleRefreshEvidence}
                    disabled={isRefreshingEvidence}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isRefreshingEvidence ? 'Refreshing evidence…' : 'Refresh evidence'}
                  </button>
                  {/* Imports draft this automatically, so the button only
                      appears when something actually needs it: a manually
                      added finding, or one whose Exposure Factor was edited
                      and no longer matches its Annex B breakdown. */}
                  {findingsNeedingRiskAnalysis > 0 && (
                    <button
                      onClick={() => handleDraftRiskAnalysis(false)}
                      disabled={isDraftingRisk}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-amber-700/60 text-amber-400 hover:bg-amber-950/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isDraftingRisk
                        ? 'Drafting…'
                        : `Draft risk analysis (${findingsNeedingRiskAnalysis})`}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-rose-800/60 text-rose-400 hover:bg-rose-950/40 transition-colors"
                >
                  Delete all ({findings.length})
                </button>
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : findings.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400 text-lg mb-2">No findings recorded</p>
                <p className="text-slate-500 text-sm">Add findings manually or import from file (CherryTree or YAML-ZIP)</p>
              </div>
            ) : (
              findings.map((finding) => (
                <div
                  key={finding.id}
                  className={`rounded-lg bg-slate-950/50 border border-slate-800 hover:border-slate-700 transition-all overflow-hidden ${finding.include_in_report === false ? 'opacity-50' : ''}`}
                >
                  {/* Header */}
                  <div className="p-4 bg-slate-900/50 border-b border-slate-800">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {finding.finding_code && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-900/30 border border-indigo-800/50 text-indigo-300 text-xs font-mono font-semibold">
                              {finding.finding_code}
                            </span>
                          )}
                          {finding.finding_category && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-violet-900/30 border border-violet-800/50 text-violet-300 text-xs font-medium">
                              {finding.finding_category === 'vulnerability' ? 'Vulnerabilidad' :
                               finding.finding_category === 'strength' ? 'Fortaleza' :
                               finding.finding_category === 'informational' ? 'Informativo' : finding.finding_category}
                            </span>
                          )}
                          {finding.finding_type && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-800/50 border border-slate-700 text-slate-300 text-xs font-medium">
                              {finding.finding_type}
                            </span>
                          )}
                          {finding.severity && (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getSeverityColor(finding.severity?.toLowerCase())}`}>
                              Severidad: {finding.severity === 'Critical' ? 'Critical' :
                               finding.severity === 'High' ? 'Alta' :
                               finding.severity === 'Medium' ? 'Media' :
                               finding.severity === 'Low' ? 'Baja' : 'Info'}
                            </span>
                          )}
                          {finding.risk !== null && finding.risk !== undefined && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-violet-500/50 bg-violet-900/30 text-violet-300">
                              Risk: {finding.risk}
                              {getRiskLevel(finding.exposure, finding.impact) && (
                                <span className="ml-1 text-violet-400">
                                  ({getRiskLevel(finding.exposure, finding.impact)})
                                </span>
                              )}
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(finding.status)}`}>
                            {finding.status}
                          </span>
                        </div>
                        <h4 className="text-base font-semibold text-indigo-400 mb-1">
                          {finding.title}
                        </h4>
                      </div>
                      <div className="flex items-center gap-3">
                        <label
                          className="flex items-center gap-2 cursor-pointer select-none group"
                          title={finding.include_in_report !== false ? 'Included in report — click to exclude' : 'Excluded from report — click to include'}
                        >
                          <input
                            type="checkbox"
                            checked={finding.include_in_report !== false}
                            onChange={async (e) => {
                              const newVal = e.target.checked;
                              setFindings(prev => prev.map(f =>
                                f.id === finding.id ? { ...f, include_in_report: newVal } : f
                              ));
                              try {
                                await offensiveApi.updateOffensiveFinding(finding.id, { include_in_report: newVal });
                              } catch (err) {
                                setFindings(prev => prev.map(f =>
                                  f.id === finding.id ? { ...f, include_in_report: !newVal } : f
                                ));
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30 focus:ring-offset-0 cursor-pointer"
                          />
                          <span className={`text-xs font-medium whitespace-nowrap ${finding.include_in_report !== false ? 'text-emerald-400' : 'text-slate-500'}`}>
                            {finding.include_in_report !== false ? 'In Report' : 'Excluded'}
                          </span>
                        </label>
                        <div className="w-px h-6 bg-slate-700" />
                        <button
                          onClick={() => handleEdit(finding)}
                          className="text-indigo-500 hover:text-indigo-600 rounded-full"
                          title="Edit finding"
                        >
                          <span className="sr-only">Edit</span>
                          <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                            <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(finding.id)}
                          className="text-rose-500 hover:text-rose-600 rounded-full"
                          title="Delete finding"
                        >
                          <span className="sr-only">Delete</span>
                          <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                            <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                            <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    {/* Description */}
                    {finding.description && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-400 uppercase mb-2">Description</h5>
                        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                          {finding.description}
                        </p>
                      </div>
                    )}

                    {/* Remediations */}
                    {(() => {
                      // Check if remediation_structured has any content
                      const hasStructuredRemediation = finding.remediation_structured && 
                        (Object.keys(finding.remediation_structured).length > 0) &&
                        ((finding.remediation_structured.correctivas && finding.remediation_structured.correctivas.length > 0) ||
                         (finding.remediation_structured.preventivas && finding.remediation_structured.preventivas.length > 0) ||
                         (finding.remediation_structured.detectivas && finding.remediation_structured.detectivas.length > 0) ||
                         (finding.remediation_structured.notes && finding.remediation_structured.notes.length > 0));
                      
                      const hasPlainRemediation = finding.remediation && finding.remediation.trim() !== '';
                      
                      if (hasStructuredRemediation || hasPlainRemediation) {
                        return (
                          <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
                            <h5 className="text-xs font-semibold text-emerald-400 uppercase mb-3">Remediaciones</h5>
                            
                            {/* Structured Remediation */}
                            {hasStructuredRemediation && (
                              <div className="space-y-4">
                                {finding.remediation_structured.correctivas && finding.remediation_structured.correctivas.length > 0 && (
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                                      <div className="text-xs font-semibold text-emerald-400">Correctivas</div>
                                    </div>
                                    <ul className="space-y-2 ml-4">
                                      {finding.remediation_structured.correctivas.map((note, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                                          <span className="text-emerald-400 mt-1 flex-shrink-0">•</span>
                                          <span className="flex-1">{note}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {finding.remediation_structured.preventivas && finding.remediation_structured.preventivas.length > 0 && (
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                      <div className="text-xs font-semibold text-blue-400">Preventivas</div>
                                    </div>
                                    <ul className="space-y-2 ml-4">
                                      {finding.remediation_structured.preventivas.map((note, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                                          <span className="text-blue-400 mt-1 flex-shrink-0">•</span>
                                          <span className="flex-1">{note}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {finding.remediation_structured.detectivas && finding.remediation_structured.detectivas.length > 0 && (
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                                      <div className="text-xs font-semibold text-amber-400">Detectivas</div>
                                    </div>
                                    <ul className="space-y-2 ml-4">
                                      {finding.remediation_structured.detectivas.map((note, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                                          <span className="text-amber-400 mt-1 flex-shrink-0">•</span>
                                          <span className="flex-1">{note}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {finding.remediation_structured.notes && finding.remediation_structured.notes.length > 0 && (
                                  <ul className="space-y-2">
                                    {finding.remediation_structured.notes.map((note, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                                        <span className="text-emerald-400 mt-1 flex-shrink-0">•</span>
                                        <span className="flex-1">{note}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                            
                            {/* Plain Remediation */}
                            {hasPlainRemediation && (
                              <div className={hasStructuredRemediation ? "mt-4 pt-4 border-t border-slate-700" : ""}>
                                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                                  {finding.remediation}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Evidence & Proof of Concept */}
                    {(finding.proof_of_concept || finding.tool_output || (finding.screenshots && finding.screenshots.length > 0)) && (
                      <div className="space-y-4 p-4 rounded-lg bg-slate-950/30 border border-slate-800">
                        <h5 className="text-xs font-semibold text-indigo-400 uppercase mb-3">Evidencia y Prueba de Concepto</h5>
                        
                        {finding.proof_of_concept && (
                          <div>
                            <h6 className="text-xs font-medium text-slate-400 mb-2">Proof of Concept</h6>
                            <div className="p-3 rounded-md bg-slate-900/50 border border-slate-800">
                              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap font-mono text-xs">
                                {finding.proof_of_concept}
                              </p>
                            </div>
                          </div>
                        )}

                        {finding.tool_output && (() => {
                          // Extract only the content between "Tool Output:" and "Remediation Notes:" (or end of string)
                          const toolOutputText = finding.tool_output;
                          let displayText = toolOutputText;
                          
                          // Try to find content after "Tool Output:"
                          const toolOutputMatch = toolOutputText.match(/Tool Output:\s*\n?(.*?)(?:\n\s*Remediation Notes:|$)/s);
                          if (toolOutputMatch) {
                            displayText = toolOutputMatch[1].trim();
                          }
                          
                          return (
                            <div>
                              <h6 className="text-xs font-medium text-slate-400 mb-2">Tool Output</h6>
                              <div className="p-3 rounded-md bg-slate-900/50 border border-slate-800 max-h-64 overflow-y-auto">
                                <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono text-xs">
                                  {displayText}
                                </pre>
                              </div>
                            </div>
                          );
                        })()}

                        {finding.screenshots && finding.screenshots.length > 0 && (
                          <div>
                            <h6 className="text-xs font-medium text-slate-400 mb-2">Capturas de Pantalla ({finding.screenshots.length})</h6>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {finding.screenshots.map((screenshot, idx) => {
                                const imageSrc = screenshot.image || screenshot;
                                return (
                                  <div key={idx} className="relative group">
                                    <img 
                                      src={imageSrc} 
                                      alt={screenshot.caption || `Screenshot ${idx + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border border-slate-800 hover:border-indigo-500 transition-colors cursor-pointer"
                                      onClick={() => {
                                        setSelectedScreenshot({ image: imageSrc, caption: screenshot.caption || `Screenshot ${idx + 1}` });
                                      }}
                                    />
                                    {screenshot.caption && (
                                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 rounded-b-lg">
                                        {screenshot.caption}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Affected Assets */}
                    {finding.affected_assets && finding.affected_assets.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-400 uppercase mb-2">Activos Afectados</h5>
                        <div className="flex flex-wrap gap-2">
                          {finding.affected_assets.map((asset, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-800/50 border border-slate-700 text-slate-300 text-xs font-mono"
                            >
                              {asset}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-4 border-t border-slate-800">
                      {finding.cve_id && (
                        <div>
                          <span className="text-slate-500 block mb-1">CVE ID</span>
                          <div className="text-slate-300 font-mono">{finding.cve_id}</div>
                        </div>
                      )}
                      {finding.discovered_at && (
                        <div>
                          <span className="text-slate-500 block mb-1">Descubierto</span>
                          <div className="text-slate-300">
                            {new Date(finding.discovered_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      )}
                      {finding.retest_required && (
                        <div>
                          <span className="text-slate-500 block mb-1">Retest</span>
                          <div className="text-amber-400 font-medium">
                            {finding.retest_status === 'required' ? 'Requerido' : 
                             finding.retest_status === 'scheduled' ? 'Programado' :
                             finding.retest_status === 'completed' ? 'Completado' : 'No requerido'}
                          </div>
                        </div>
                      )}
                      {finding.cvss_vector && (
                        <div>
                          <span className="text-slate-500 block mb-1">CVSS Vector</span>
                          <div className="text-slate-300 font-mono text-xs break-all">{finding.cvss_vector}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Screenshot Lightbox Modal */}
      {selectedScreenshot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <div className="relative max-w-7xl w-full max-h-[90vh] flex flex-col">
            <button
              onClick={() => setSelectedScreenshot(null)}
              className="absolute top-4 right-4 z-10 text-white hover:text-slate-300 transition-colors bg-black/50 rounded-full p-2"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex-1 overflow-auto flex items-center justify-center p-4">
              <img 
                src={selectedScreenshot.image} 
                alt={selectedScreenshot.caption}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
            {selectedScreenshot.caption && (
              <div className="p-4 bg-slate-900/90 text-white text-sm text-center">
                {selectedScreenshot.caption}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowBulkDeleteModal(false)}
          ></div>
          <div className="relative max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 fill-current text-rose-500" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Delete all {findings.length} findings?
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Every finding on this engagement will be permanently removed, including any
                  edits, evidence and risk estimations. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleBulkDeleteConfirm}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting…' : `Delete all ${findings.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => {
              setShowDeleteModal(false);
              setFindingToDelete(null);
            }}
          ></div>
          <div className="relative max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 fill-current text-amber-500" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Are you sure you want to delete this finding? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                onClick={() => {
                  setShowDeleteModal(false);
                  setFindingToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 transition-colors"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FindingsManagementModal;
